import { useState } from "react";
import { Button } from "src/vibe-code/rcm-v2/components/ui/button";
import { Input } from "src/vibe-code/rcm-v2/components/ui/input";
import { Label } from "src/vibe-code/rcm-v2/components/ui/label";
import { Textarea } from "src/vibe-code/rcm-v2/components/ui/textarea";
import { PhoneInput } from "src/vibe-code/rcm-v2/components/ui/phone-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/vibe-code/rcm-v2/components/ui/select";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/vibe-code/rcm-v2/components/ui/card";
import { Checkbox } from "src/vibe-code/rcm-v2/components/ui/checkbox";
import { Separator } from "src/vibe-code/rcm-v2/components/ui/separator";
import {
  type Employee,
  type Dealer,
  getEmployeeRoleOptions,
} from "src/vibe-code/rcm-v2/types";
import {
  parseIsoDate,
  isValidDate,
  isValidPhoneNumberWithCountryCode,
} from "src/utils";

interface EmployeeFormProps {
  employee?: Employee;
  dealers: Dealer[];
  selectedDealerId?: string;
  onSave: (employee: any) => void;
  onCancel: () => void;
}

export function EmployeeForm({
  employee,
  dealers,
  selectedDealerId,
  onSave,
  onCancel,
}: EmployeeFormProps) {
  const formatBirthdateForForm = (isoDate: string | undefined) => {
    if (!isoDate || !isValidDate(isoDate)) return "";
    const { year, month, day } = parseIsoDate(isoDate);
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  };
  const [formData, setFormData] = useState({
    id: employee?.id || undefined,
    employeeFirstName: employee?.first_name || "",
    employeeLastName: employee?.last_name || "",
    title: employee?.title || "",
    role: employee?.role || "",
    email: employee?.email || "",
    directLine: employee?.phone_number || "",
    birthday: formatBirthdateForForm(employee?.birthday) || "",
    dealerId: employee?.dealer.id || selectedDealerId || "",
    employeeProfileNotes: employee?.profile_notes || "",
    giftSent: employee?.gift_sent || false,
    lastActivityDate: employee?.last_activity || "",
    additionalEmailAddresses: employee?.additional_emails?.join(", ") || "",
  });
  const [errors, setErrors] = useState<{ directLine?: string }>({});

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (field === "directLine" && errors.directLine) {
      setErrors((prev) => ({ ...prev, directLine: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate phone number if provided
    const newErrors: { directLine?: string } = {};
    if (formData.directLine && formData.directLine.trim()) {
      if (!isValidPhoneNumberWithCountryCode(formData.directLine.trim())) {
        newErrors.directLine = "Phone number should be 10 digits";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Add dealer name and store number based on selected dealer
    const selectedDealer = dealers.find((d) => d.id === formData.dealerId);
    const employeeData = {
      ...formData,
      dealerId: formData.dealerId, // API expects dealerId
      additionalEmailAddresses: formData.additionalEmailAddresses
        ? formData.additionalEmailAddresses
            .split(",")
            .map((email: string) => email.trim())
            .filter((email: string) => email)
        : [],
    };

    if (!selectedDealer) {
      alert("Please select a dealer");
      return;
    }
    if (!employeeData.role) {
      alert("Please select a role");
      return;
    }

    onSave(employeeData);
  };

  const selectedDealer = dealers.find((d) => d.id === formData.dealerId);

  return (
    <div className="p-6 mx-auto max-w-2xl">
      <Card className="shadow-custom-lg">
        <CardHeader>
          <CardTitle className="text-2xl">
            {employee ? "Edit Employee" : "Add New Employee"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dealer Selection */}
            <div>
              <Label htmlFor="dealerId">Associated Dealer *</Label>
              <Select
                value={formData.dealerId}
                onValueChange={(value: string) =>
                  handleInputChange("dealerId", value)
                }
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a dealer" />
                </SelectTrigger>
                <SelectContent>
                  {dealers.map((dealer) => (
                    <SelectItem
                      key={dealer.id}
                      value={dealer.id}
                      selected={
                        employee?.dealer && dealer.id === employee.dealer.id
                      }
                    >
                      {dealer.name}
                      {dealer.store_number ? ` (${dealer.store_number})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedDealer && (
                <div className="p-2 mt-2 text-sm rounded bg-muted">
                  <span className="font-medium">Store Number:</span>{" "}
                  {selectedDealer.store_number || "Not set"}
                </div>
              )}
            </div>

            <Separator />

            {/* Contact Information */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="employeeFirstName">Employee First Name *</Label>
                <Input
                  id="employeeFirstName"
                  value={formData.employeeFirstName}
                  onChange={(e) =>
                    handleInputChange("employeeFirstName", e.target.value)
                  }
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="employeeLastName">Employee Last Name *</Label>
                <Input
                  id="employeeLastName"
                  value={formData.employeeLastName}
                  onChange={(e) =>
                    handleInputChange("employeeLastName", e.target.value)
                  }
                  placeholder="Enter last name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  placeholder="Enter job title"
                  required
                />
              </div>
              <div>
                <Label htmlFor="role">Role (Dealer Portal Permissions) *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value: string) =>
                    handleInputChange("role", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {getEmployeeRoleOptions().map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        selected={formData.role === option.value}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="contact@email.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="directLine">Direct Line/Cell</Label>
                <PhoneInput
                  id="directLine"
                  value={formData.directLine}
                  onChange={(value) => handleInputChange("directLine", value)}
                  placeholder="210-555-1234"
                  className={errors.directLine ? "border-red-500" : ""}
                />
                {errors.directLine && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.directLine}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="birthday">Birthday (Optional)</Label>
                <Input
                  id="birthday"
                  type="date"
                  value={formData.birthday}
                  onChange={(e) =>
                    handleInputChange("birthday", e.target.value)
                  }
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="additionalEmailAddresses">
                  Additional Email Addresses
                </Label>
                <Input
                  id="additionalEmailAddresses"
                  value={formData.additionalEmailAddresses}
                  onChange={(e) =>
                    handleInputChange(
                      "additionalEmailAddresses",
                      e.target.value,
                    )
                  }
                  placeholder="email1@domain.com, email2@domain.com"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Separate multiple emails with commas
                </p>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="employeeProfileNotes">
                  Employee Profile Notes
                </Label>
                <Textarea
                  id="employeeProfileNotes"
                  value={formData.employeeProfileNotes}
                  onChange={(e) =>
                    handleInputChange("employeeProfileNotes", e.target.value)
                  }
                  placeholder="Add any notes about this employee..."
                  rows={3}
                />
              </div>
            </div>

            <Separator />

            {/* Permissions & Settings */}
            <div className="space-y-4">
              <Label className="text-base font-medium">
                Permissions & Settings
              </Label>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="giftSent"
                    checked={formData.giftSent}
                    onCheckedChange={(checked: boolean) =>
                      handleInputChange("giftSent", !!checked)
                    }
                  />
                  <Label htmlFor="giftSent" className="text-sm font-normal">
                    Gift Sent - A gift has been sent to this employee
                  </Label>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end pt-6 space-x-3 border-t">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button type="submit" className="text-white gradient-primary">
                {employee ? "Update Employee" : "Create Employee"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
